import {
    ISectionContentStyle, ISectionStyle, IMeshedSectionContentData,
    ISectionWithMeshData, IScreenData, ScrollEffectName
} from "@pageBuilder/typings";
import { CssProperties } from "../../models/css-property";
import { ElementUtil } from "../../utils/element-util";
import { RendererBase } from "../../shared/renderer-base";
import { MeshRenderer } from "../../mesh/renderers/mesh-renderer";
import { BackgroundDataRenderer } from "../../shared/rendering/background-data-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";

export abstract class MeshSectionRendererBase extends RendererBase<ISectionWithMeshData> {

    protected readonly backgroundRenderer: BackgroundDataRenderer;
    protected readonly meshRenderer: MeshRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.meshRenderer = new MeshRenderer();
        this.backgroundRenderer = new BackgroundDataRenderer(this.styleService);
    }

    protected createNewInternal(): HTMLElement {
        return this.createNewSection();
    }

    protected renderInternal(element: HTMLElement, data: ISectionWithMeshData): HTMLElement {
        if (data.sectionName) {
            element.setAttribute("section-name", data.sectionName);
        }

        if (data?.favorite) {
            element.setAttribute("favorite", "");
        } else {
            element.removeAttribute("favorite");
        }

        this.renderStyles(element, data.style);

        return element;
    }

    protected renderSectionVisibility(element: HTMLElement, data: ISectionWithMeshData): void {
        if (!data.sectionVisibility) {
            return;
        }

        ElementUtil.setVisibilityClasses(element, data.sectionVisibility);
    }

    protected renderScreen(element: HTMLElement, data: IScreenData): HTMLElement {
        if (data.id) {
            element.id = data.id;
        }

        this.backgroundRenderer.renderBackground(element, data.background, undefined, "section background");
        this.renderContent(element, data.content);

        return element;
    }

    protected renderBackgroundScrollEffectName(element: HTMLElement, effect: ScrollEffectName | undefined): void {
        element.dataset.bgScrollEffect = effect ?? "none";
    }

    protected removeBackgroundInlineStyles(section: HTMLElement): void {
        const background = section.querySelector(":scope > .slider-content > .slides-wrapper > .slide > .background, :scope > .background");
        const backgroundMedia = background?.querySelector(":scope > img, :scope > video");
        background?.removeAttribute("style");
        backgroundMedia?.removeAttribute("style");
    }

    protected cleanupScreen(element: HTMLElement): HTMLElement {
        const background = element.querySelector(":scope > .background");
        const content = element.querySelector(":scope > .content");

        this.cleanupScreenMesh(element);
        this.cleanScreenStyles(element);

        background?.remove();
        content?.remove();

        return element;
    }

    protected cleanupScreenMesh(element: HTMLElement, content?: HTMLElement): void {
        const contentElement = content ?? element.querySelector(":scope > .content");

        const mesh = contentElement?.querySelector("[data-mesh]") as HTMLElement;
        if (mesh) {
            this.meshRenderer.cleanup(mesh);
        }
    }

    private renderContent(element: HTMLElement, data: IMeshedSectionContentData): void {
        let content = element.querySelector(".content");
        if (!content) {
            content = this.createContentElement();

            const backgroundElement = element.querySelector(":scope > .background");
            if (backgroundElement) {
                backgroundElement?.after(content);
            } else {
                element.append(content);
            }
        }

        const mesh = element.querySelector(`#${data.mesh.id}`) as HTMLElement; // we render mesh because some fields changed through section settings (fixme)
        if (mesh && data.mesh) {
            this.meshRenderer.render(mesh, data.mesh);
        }

        if (!ElementUtil.isSectionWithoutContentBackground(element)) {
            this.backgroundRenderer.renderBackground(element, data.background, ".content .background", "section content background");
        }

        this.renderContentStyles(element, data.style);
    }

    protected createContentElement(): HTMLElement {
        const content = document.createElement("div");
        content.classList.add("content");

        return content;
    }

    private renderContentStyles(element: HTMLElement, style?: ISectionContentStyle | null): void {
        this.cleanupContentStyles(element);

        const content = element.querySelector(".content");

        if (!style || !content) {
            return;
        }

        const cssItems = new CssProperties();

        if (style?.borderRadius) {
            cssItems.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            cssItems.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            cssItems.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            cssItems.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        if (style?.displayShadow) {
            cssItems.tryAdd("box-shadow", `0px 5px 22px ${style.shadowColor}`);
        }

        if (style.maxWidthInPx) {
            cssItems.tryAdd("max-width", `${style.maxWidthInPx}px`);
        }

        if (style.upperAndBottomMarginsInPc) {
            cssItems.tryAddAll(["margin-top", "margin-bottom"], `${style.upperAndBottomMarginsInPc}vh`);
        }

        if (cssItems.data.length > 0) {
            this.styleService.setStyle(element, `#${element.id} .content`, cssItems.data);
        }
    }

    protected renderStyles(element: HTMLElement, style?: ISectionStyle | null): void {
        if (!style) {
            return;
        }

        const cssItems = this.getBaseStyleCssItems(style);
        this.styleService.setStyle(element, `#${element.id}`, cssItems.data);
    }

    protected getBaseStyleCssItems(style: ISectionStyle): CssProperties {
        const cssItems = new CssProperties();
        cssItems.tryAdd("min-height", `${style.minHeightInPc}vh`);

        return cssItems;
    }

    protected getSectionName(sectionElement: HTMLElement): string | null {
        return sectionElement.getAttribute("section-name");
    }

    protected getBackgroundScrollEffectName(sectionElement: HTMLElement): ScrollEffectName {
        const effectRow = sectionElement.dataset.bgScrollEffect;
        if (effectRow) {
            return effectRow as ScrollEffectName;
        }
        return "none";
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        const selectors = [ this.getCommonStyleSelector(element) ];
        selectors.push(this.getContentStyleSelector(element));
        selectors.push(...this.backgroundRenderer.getContentSelectorsInternal(element));
        selectors.push(...this.backgroundRenderer.getContentSelectorsInternal(element, ".content .background"));
        return selectors;
    }

    private cleanScreenStyles(element: HTMLElement): void {
        const selectors = this.getContentSelectorsInternal(element);
        selectors.forEach(selector => this.styleService.clearStyle(element, selector));
    }

    protected getCommonStyleSelector(element: HTMLElement): string {
        return `#${element.id}`;
    }

    protected getContentStyleSelector(element: HTMLElement): string {
        return `#${element.id} .content`;
    }

    protected cleanupContentStyles(element: HTMLElement): void {
        this.styleService.clearStyle(element, this.getContentStyleSelector(element));
    }

    protected abstract createNewSection(): HTMLElement;

    // protected abstract getSectionData(element: HTMLElement): ISectionWithMeshData;

}
